import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface ReportWidget {
  id: string;
  type: 'kpi' | 'status_pie' | 'type_bar' | 'timeline' | 'sla' | 'team' | 'productivity' | 'executive_summary';
  label: string;
  enabled: boolean;
  order: number;
}

export interface ReportFilters {
  dateRange?: { start: string; end: string };
  ticketTypes?: string[];
  priorities?: string[];
  statuses?: string[];
  atendenteIds?: string[];
}

export interface ReportTemplate {
  id: string;
  nome: string;
  descricao: string | null;
  client_id: string | null;
  created_by: string | null;
  widgets: ReportWidget[];
  filtros: ReportFilters;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export const DEFAULT_WIDGETS: ReportWidget[] = [
  { id: 'executive_summary', type: 'executive_summary', label: 'Resumo Executivo', enabled: true, order: 0 },
  { id: 'kpi', type: 'kpi', label: 'Cards de Métricas', enabled: true, order: 1 },
  { id: 'sla', type: 'sla', label: 'Performance SLA', enabled: true, order: 2 },
  { id: 'productivity', type: 'productivity', label: 'Produtividade', enabled: true, order: 3 },
  { id: 'team', type: 'team', label: 'Performance de Equipe', enabled: true, order: 4 },
  { id: 'status_pie', type: 'status_pie', label: 'Distribuição por Status', enabled: true, order: 5 },
  { id: 'type_bar', type: 'type_bar', label: 'Tickets por Tipo', enabled: true, order: 6 },
  { id: 'timeline', type: 'timeline', label: 'Evolução Diária', enabled: true, order: 7 },
];

export const useReportTemplates = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['report-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('report_templates' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as ReportTemplate[];
    },
  });

  const createTemplate = useMutation({
    mutationFn: async (template: { nome: string; descricao?: string; widgets: ReportWidget[]; filtros: ReportFilters }) => {
      const { data: userData } = await supabase.from('users').select('client_id').eq('id', user?.id || '').single();
      
      const { data, error } = await supabase
        .from('report_templates' as any)
        .insert({
          nome: template.nome,
          descricao: template.descricao || null,
          widgets: JSON.parse(JSON.stringify(template.widgets)),
          filtros: JSON.parse(JSON.stringify(template.filtros)),
          client_id: userData?.client_id || null,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-templates'] });
      toast.success('Template salvo com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao salvar template');
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('report_templates' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-templates'] });
      toast.success('Template removido!');
    },
    onError: () => {
      toast.error('Erro ao remover template');
    },
  });

  return {
    templates,
    isLoading,
    createTemplate,
    deleteTemplate,
  };
};
